<template>
  <section class="pin-tab">
    <article class="no-print">
      <div v-if="!viewingOwnProfile && currentUserPin && !checkingSwap" class="swap-button-container">
        <button class="swap-button button is-primary is-uppercase" @click="initiateSwapRequest" :disabled="userHasSwapped">{{ swapButtonText }}</button>
      </div>
      <div v-if="viewingOwnProfile" class="pin-controls is-flex is-pulled-right">
        <button v-if="!reorderMode" class="button is-uppercase is-small" :disabled="emptyView" @click="reorderMode = true">{{ $t('sectionProfile.pinsTab.reorder') }}</button>
        <button v-if="reorderMode" class="button is-uppercase is-small" @click="savePinOrder">{{ $t('sectionProfile.pinsTab.savePinOrder') }}</button>
        <button class="button is-uppercase is-small" :disabled="emptyView" @click="exportPins">{{ $t('sectionProfile.pinsTab.export') }}</button>
      </div>
    </article>
    <article v-if="!emptyView">
      <h1 v-if="isExportMode" class="title has-text-primary has-text-centered is-uppercase">{{ pinsExportText }}</h1>
      <infinite-scroll class="pins-container" :callback="getPins" distance="5">
        <ul>
          <li v-for="(userPin, index) in userPins" :key="index" class="cursor-pointer" @click="viewProfile(userPin.pin.user.userName)">
            <div class="pin-container" :class="{'cursor-pointer': reorderMode, 'print-mode': isExportMode}"
                 :draggable="reorderMode" :id="`pin-${userPin.pin.id}`" :data-post="userPin"
                 :data-offset="index" @dragstart="drag" @dragover="allowDrop" @drop="drop">
              <img v-if="isMyPin(userPin.pin)" class="pin-background" :class="{'print-mode': isExportMode }" :src="staticPinBackground(true)">
              <img v-if="!isMyPin(userPin.pin)" class="pin-background" :class="{'print-mode': isExportMode }" :src="staticPinBackground(false)">
              <div class="pin-image" v-bind:style="getBackgroundStyle(userPin.pin.url)"></div>
            </div>
          </li>
        </ul>
        <div v-show="loading" class="loader"></div>
      </infinite-scroll>
    </article>
    <article v-if="!loading && emptyView">
      <p>{{placeholder}}</p>
    </article>
    <swap-pin-warning ref="swapWarningModal" :userName="user.userName" @modalSuccess="sendSwapRequest"></swap-pin-warning>
  </section>
</template>

<script>
import state from '../state'
import InfiniteScroll from '@/components/Controls/InfiniteScroll'
import SwapPinWarning from '@/components/Modals/SwapPinWarning'

import imageProxy from '@/libs/image-proxy'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'

import i18n from '@/i18n'

export default {
  mixins: [auth, componentState],
  components: {
    InfiniteScroll,
    SwapPinWarning
  },
  props: ['user', 'profile'],
  data: function () {
    return {
      limit: 16,
      offset: 0,
      total: 0,
      hasMore: true,
      loading: false,
      userPins: [],
      dragging: { to: null, from: null },
      reorderMode: false,
      checkingSwap: false,
      userHasSwapped: true,
      isExportMode: false,
      savedPageTitle: ''
    }
  },
  computed: {
    placeholder: function () {
      return this._get(state.editable.profile, false) ? i18n.t('sectionProfile.pinsTab.pinsAppearHere') : ''
    },
    viewingOwnProfile: function () {
      return this.user.id === this.currentUser.id
    },
    emptyView: function () {
      return this.total === 0
    },
    currentUserPin: function () {
      return this.$store.getters.currentUserPin
    },
    swapButtonText: function () {
      return this.userHasSwapped ? i18n.t('sectionProfile.pinsTab.alreadySwapped') : i18n.t('sectionProfile.pinsTab.swapPins')
    },
    pinsExportText: function () {
      return 'My ' + this.$appName + ' 2022 Pins'
    }
  },
  watch: {
    user: function () {
      this.resetPins()
    }
  },
  mounted: function () {
    this.resetPins()
  },
  methods: {
    getPins: function () {
      if (this.loading || !this.hasMore || !this.user.userName) {
        return
      }

      this.loading = true
      const data = { username: this.user.userName, limit: this.limit, offset: this.offset }
      this.$store.dispatch('getUserPins', data).then((response) => {
        this.userPins.push.apply(this.userPins, response.pins)
        this.total = response.total
        this.hasMore = response.hasMore
        this.loading = false
        this.offset += this.limit
      }, (error) => {
        console.error(error)
        this.loading = false
      })
    },
    checkUsersSwapped: function () {
      this.checkingSwap = true
      const data = { viewingUser: this.currentUser.userName, viewedUser: this.user.userName }
      this.$store.dispatch('getUserHasSwapped', data).then((swappedPin) => {
        if (!swappedPin) {
          this.userHasSwapped = false
        }
        this.checkingSwap = false
      }, (error) => {
        this.checkingSwap = false
        console.error(error)
      })
    },
    initiateSwapRequest: function () {
      this.$store.dispatch('getUserSetting', 'showPinSwapWarning').then((showPinSwapWarningSetting) => {
        if (showPinSwapWarningSetting) {
          this.$refs.swapWarningModal.showModal()
        } else {
          this.sendSwapRequest()
        }
      })
    },
    sendSwapRequest: function () {
      const data = { sendingUser: this.currentUser.userName, receivingUser: this.user.userName }
      this.$store.dispatch('sendPinSwapRequest', data).then((response) => {
        this.userHasSwapped = true
        this.resetPins()
      }, (error) => {
        console.error(error)
      })
    },
    thumbnail: function (image) {
      return imageProxy.proxy(image, { width: 200, height: 200 })
    },
    getBackgroundStyle: function (image) {
      const style = {}
      style.backgroundImage = `url('${this.thumbnail(image)}')`
      return style
    },
    resetPins: function () {
      this.offset = 0
      this.total = 0
      this.hasMore = true
      this.loading = false
      this.userPins = []
      this.reorderMode = false
      this.checkingSwap = false
      this.getPins()
      this.checkUsersSwapped()
    },
    drag: function (event) {
      if (!this.reorderMode) {
        return
      }
      this.dragging.from = parseInt(event.target.dataset.offset)
      // firefox has a bug requiring that you set data in the drag start
      // this is ignored by event.preventDefault() in the drop event
      event.dataTransfer.setData('text', '')
    },
    canDrop: function (to) {
      return !isNaN(to) && this.dragging.from !== null && this.dragging.from !== to
    },
    allowDrop: function (event) {
      const to = parseInt(event.target.dataset.offset)
      if (this.canDrop(to)) {
        event.preventDefault()
        this.dragging.to = to
        this.drop(event)
      }
    },
    drop: function (event) {
      event.preventDefault()
      const newPins = this.userPins.slice()
      newPins.splice(this.dragging.to, 0, newPins.splice(this.dragging.from, 1)[0])
      this.dragging.from = this.dragging.to
      this.dragging.to = null
      this.userPins = newPins.slice()
    },
    savePinOrder: function () {
      const userPinIDs = this.userPins.map((userPin) => { return userPin.id })
      this.$store.dispatch('updatePinOrder', userPinIDs).then(() => {
        this.resetPins()
      })
    },
    exportPins: async function () {
      this.limit = this.total
      await this.getPins()
      this.isExportMode = true
      this.$nextTick(() => {
        this.savedPageTitle = document.title
        document.title = this.user.userName + '\'s ProjectBoard Pins'
        window.print()
      })
    },
    finishExport: function () {
      this.limit = 16
      document.title = this.savedPageTitle
      this.isExportMode = false
    },
    viewProfile: function (username) {
      this.$router.push({ name: 'profile', params: { username, tab: 'pins' } })
    },
    staticPinBackground: function (isPrimary) {
      if (isPrimary) {
        return 'https://firebasestorage.googleapis.com/v0/b/project-leo-mvp.appspot.com/o/staticImages%2FpinBackground-primary.png?alt=media&token=eb463fc6-67b5-4e89-888f-174372865219'
      }
      return 'https://firebasestorage.googleapis.com/v0/b/project-leo-mvp.appspot.com/o/staticImages%2FpinBackground.png?alt=media&token=313d6624-1c1c-4c4f-a97b-5dbe2e4fdfec'
    },
    isMyPin: function (pin) {
      return pin.user.id === this.currentUser.id
    }
  },
  created: function () {
    window.addEventListener('afterprint', (event) => {
      this.finishExport()
    })
  },
  destroyed: function () {
    window.removeEventListener('afterprint', (event) => {
      this.finishExport()
    })
  }

}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

ul {
  width: 100%;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: start;
  -ms-flex-pack: start;
  justify-content: flex-start;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  margin-right: 0;
  @media screen and (max-width: 600px) {
    justify-content: space-around;
    width: 100%;
  }
  margin-left: 2rem

  li {
    width: 250px;
    height: 250px;
    text-align: center;
    margin: 1em;
  }
}

.swap-button-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  .swap-button {
    font-size: 0.85rem;
    padding: 1.25rem 1.5rem;
  }
}
.pin-controls {
  button {
    margin-left: 0.5rem;
    font-weight: 500;
  }
}

.slide-enter-active {
  animation: slide-in 1s ease-out forwards;
}

.slide-leave-active {
  animation: slide-out 0.5s ease-out forwards;
}

@keyframes slide-in {
  from {
    transform: translateY(45vh);
    opacity:0;
  }
  to {
    transform: translateY(0);
    opacity:1;
  }
}

@keyframes slide-out {
  from {
    transform: translateY(0px);
    opacity:1;
  }
  to {
    transform: translateY(20vh);
    opacity:0;
  }
}

.slide-move{
  transition: transform 1s;
}
.pin-container {
  height: 300px;
  &.print-mode {
     height: 250px;
   }
  .pin-background {
    margin: 0 2rem;
    min-height: 250px;
    min-width: 250px;
    height: 250px;
    width: 250px;
    max-height: 250px;
    max-width: 250px;
    @media (max-width: 800px) {
      margin-top: 1.5rem;
    }
    &.print-mode {
      margin: 0 1rem;
    }
  }
  .pin-image {
    position: relative;
    top: -14.75rem;
    margin-left: auto;
    margin-right: auto;
    background-size: cover;
    border-radius: 50%;
    height: 200px;
    width: 200px;
    max-height: 200px;
    max-width: 200px;
  }
}
.loader {
  display: block;
  font-size: 3rem;
  margin: 2rem auto 4rem;
}
.pin-tab {
  @include mobile {
    padding-bottom: 5rem;
  }
}
</style>
